// src/receptionist/components/WhatsappOnboarding.tsx
//
// The WhatsApp half of Business profile (ourteam-frontend#3613, BE
// ourteam-backend#4671): the connection card, the onboarding checklist, the
// consent to the one-way door, the move onto the API, and the template pack
// with Meta's approval state per template.
//
// Everything here comes from GET .../whatsapp, not from MerchantConfig. The
// config's `whatsappConnected` and `approvedTemplates` are projections of this
// payload, and rendering both would let the screen show two answers that
// disagree. This card is the source; the merchant-config fields are not read.
//
// Shared-number mode (decision on ourteam-backend#4666, 3 Sept 2026): for now
// every merchant runs on ourteam's own number. `onPlatformNumber: true` means
// the number-related checks are already done (they belong to that number),
// the move is not applicable, and what the merchant needs is the entry link
// to share — the tag in it is what routes a customer to them.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    AlertTriangleIcon, CheckIcon, CopyIcon,
    FileTextIcon, ListChecksIcon, Loader2Icon, PencilLineIcon, PhoneIcon, SendIcon, XCircleIcon,
} from "lucide-react";
import { Card, CardHeader, CardBody, Button } from "./kit";
import { Input } from "@component/input";
import { cn } from "@oura/lib/cn";
import { useSnackbar } from "@hook/use-snackbar";
import { TagPill } from "./ui";
import { forgetSetupState } from "./SetupBanner";
import { PillLink, TYPE, type Tone } from "@oura/ui";
import {
    getWhatsappState, acceptWhatsappTerms, startWhatsappMigration, submitTemplates,
    type WhatsappState, type WhatsappTemplate, type WhatsappTemplateKey, type OnboardingStage,
} from "@service/receptionist";

const POLL_MS = 10_000;

/** The setup steps, in order. The banner above every screen names the ones still open (#4789). */
export const SETUP_CHECKLIST: { key: keyof WhatsappState["checklist"]; label: string }[] = [
    { key: "detailsConfirmed", label: "Business details confirmed" },
    { key: "metaVerified", label: "Meta Business verification" },
    { key: "numberMigrated", label: "Number moved to the WhatsApp Business API" },
    { key: "templatesApproved", label: "Message templates approved" },
    { key: "kbSignedOff", label: "Knowledge base signed off" },
];

const STAGE_LABEL: Record<OnboardingStage, string> = {
    details: "Business details",
    meta_verification: "Meta verification",
    number_migration: "Moving the number",
    templates_pending: "Templates pending",
    kb_signoff: "Awaiting sign-off",
    live: "Live",
};

const TEMPLATE_LABEL: Record<WhatsappTemplateKey, string> = {
    booking_confirmed: "Booking confirmed",
    reminder_24h: "Day-before reminder",
    reminder_same_day: "Same-day reminder",
    stall_nudge: "Nudge after silence",
    post_visit_rebook: "Post-visit rebooking",
    no_show_recovery: "Missed appointment",
    morning_digest: "Morning digest (to your team)",
};

const TEMPLATE_STATUS: Record<WhatsappTemplate["status"], { label: string; tone: Tone }> = {
    approved: { label: "Approved", tone: "green" },
    pending: { label: "Awaiting Meta", tone: "amber" },
    not_submitted: { label: "Not submitted", tone: "gray" },
    rejected: { label: "Rejected", tone: "red" },
};

/** The stages that are still someone else's turn — Meta's, mostly. */
function isWaiting(s: WhatsappState): boolean {
    return s.onboardingStage === "meta_verification"
        || s.onboardingStage === "number_migration"
        || s.onboardingStage === "templates_pending"
        || s.templates.some((t) => t.status === "pending");
}

function StageChip({ stage }: { stage: OnboardingStage | string }) {
    const label = (STAGE_LABEL as Record<string, string>)[stage] ?? stage;
    return (
        <TagPill tone={stage === "live" ? "green" : "amber"}>{label}</TagPill>
    );
}

/** Oura's step mark: a filled green check when done, a blue ring and dot on the next step, an empty grey ring after it. */
function SetupMark({ done, next }: { done: boolean; next: boolean }) {
    if (done) {
        return (
            <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                <CheckIcon size={12} strokeWidth={3} />
            </span>
        );
    }
    return (
        <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2 bg-white", next ? "border-blue-600" : "border-gray-300")}>
            <span className={cn("h-2 w-2 rounded-full", next ? "bg-blue-600" : "bg-transparent")} />
        </span>
    );
}

/** The one fact on the right of a setup row, as Oura's steps carry a date or a count. */
function setupFact(key: keyof WhatsappState["checklist"], s: WhatsappState): string | null {
    const done = s.checklist[key];
    switch (key) {
        case "detailsConfirmed":
            return done ? "Done" : "Save your details";
        case "metaVerified":
        case "numberMigrated":
            return s.onPlatformNumber ? "On ourteam's number" : done ? "Done" : "With Meta";
        case "templatesApproved": {
            const approved = s.templates.filter((t) => t.status === "approved").length;
            return `${approved} of ${s.templates.length} approved`;
        }
        case "kbSignedOff":
            return done ? (s.kbSignedOffAt ? `Signed off ${when(s.kbSignedOffAt)}` : "Signed off") : "Sign off on Services";
    }
}

function when(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/**
 * The entry link, presented exactly as the WhatsApp ad-link dialog presents
 * its own (containers/dialog/dialog-whatsapp-ad-link.tsx): a label (sentence case, Oura),
 * a read-only monospace field that selects on focus, and a solid Copy button.
 * It is the same job — a link the merchant pastes elsewhere — so it should not
 * be a different control.
 */
function CopyLink({ value }: { value: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard denied (insecure context, permissions). The field is
            // selectable, so the link is still gettable; nothing else to do.
        }
    };
    return (
        <div className="space-y-1.5">
            <label className={TYPE.lab}>Your link</label>
            <div className="flex items-center gap-2">
                <Input
                    readOnly
                    value={value}
                    onFocus={(e) => e.currentTarget.select()}
                    className="font-mono text-xs"
                />
                <Button onClick={() => void copy()} className="flex-shrink-0">
                    {copied ? <><CheckIcon className="h-4 w-4" /> Copied</> : <><CopyIcon className="h-4 w-4" /> Copy</>}
                </Button>
            </div>
        </div>
    );
}

/**
 * The one-way door, as a question. Same warning copy the card carries; the
 * merchant confirms it once and the acceptance is recorded server-side.
 */
function ConsentDialog({ open, busy, onCancel, onConfirm }: { open: boolean; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="wa-consent-title">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/[0.04]">
                <h2 id="wa-consent-title" className={cn(TYPE.tt, "m-0 text-gray-900")}>Move this number onto the WhatsApp Business API?</h2>
                <p className="mt-2 text-sm text-gray-700">
                    Once it moves, the number leaves the WhatsApp Business app on your phone. Your team works from the
                    staff group and this console instead. Moving back means contacting Meta.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" onClick={onCancel} disabled={busy}>Not now</Button>
                    <Button onClick={onConfirm} disabled={busy}>{busy ? "Starting…" : "I understand, move it"}</Button>
                </div>
            </div>
        </div>
    );
}

/**
 * `show` picks the part for a Business profile tab (#4785): the number and the
 * setup checklist, or the template pack. Without it, all three cards.
 */
export function WhatsappOnboarding({ merchantId, typedNumber, show, followUpsHref }: {
    merchantId: string;
    typedNumber: string;
    show?: "number" | "whatsapp" | "templates";
    /** Conversation → Follow-ups, where each template's wording is written. */
    followUpsHref?: string;
}) {
    const snackbar = useSnackbar();
    const [state, setState] = useState<WhatsappState | "loading" | "error">("loading");
    const [consentOpen, setConsentOpen] = useState(false);
    const [busy, setBusy] = useState<"migrate" | "submit" | null>(null);
    const seq = useRef(0);

    const load = useCallback(async (quiet = false) => {
        const mine = ++seq.current;
        if (!quiet) setState("loading");
        try {
            const s = await getWhatsappState(merchantId);
            if (seq.current === mine) setState(s);
        } catch {
            if (seq.current === mine && !quiet) setState("error");
        }
    }, [merchantId]);

    useEffect(() => { void load(); }, [load]);

    // Arriving from the "Setup is not finished" banner: bring the Setup card into view once it is drawn.
    const { hash } = useLocation();
    const drawn = state !== "loading" && state !== "error";
    useEffect(() => {
        if (drawn && hash === "#setup") document.getElementById("setup")?.scrollIntoView({ block: "start" });
    }, [drawn, hash]);

    // Meta's verification, the number move and template review are all slow
    // and human-in-the-loop. While any of them is in flight, ask again every
    // ten seconds so an approval reaches the screen without a manual refresh.
    // Stopping at templates_pending would have meant it never did.
    useEffect(() => {
        if (typeof state === "string" || !isWaiting(state)) return;
        const t = window.setInterval(() => void load(true), POLL_MS);
        return () => window.clearInterval(t);
    }, [state, load]);

    const migrate = async () => {
        if (typeof state === "string") return;
        setBusy("migrate");
        try {
            if (!state.oneWayDoorAcceptedAt) await acceptWhatsappTerms(merchantId);
            const result = await startWhatsappMigration(merchantId);
            if (result.ok) {
                setState(result.state);
                setConsentOpen(false);
                snackbar.showSuccess("The move has started.", "This takes Meta a while; the checklist updates on its own.");
            } else if (result.kind === "terms_not_accepted") {
                // The server has not recorded consent: say so and ask again.
                snackbar.showError(result.message);
                setConsentOpen(true);
            } else {
                snackbar.showError(result.message);
            }
        } catch {
            snackbar.showError("Could not record your consent. Please try again.");
        } finally {
            setBusy(null);
        }
    };

    const submit = async () => {
        if (typeof state === "string") return;
        // Rejected ones go again too (#6679: they were refused for a missing example, not their wording).
        const keys = state.templates.filter((t) => t.status === "not_submitted" || t.status === "rejected").map((t) => t.key);
        if (!keys.length) return;
        setBusy("submit");
        try {
            setState(await submitTemplates(merchantId, keys));
            forgetSetupState(merchantId);
            snackbar.showSuccess(`${keys.length} ${keys.length === 1 ? "template" : "templates"} sent to Meta for approval.`);
        } catch {
            snackbar.showError("Could not submit the templates. Please try again.");
        } finally {
            setBusy(null);
        }
    };

    if (state === "loading") {
        return (
            <Card>
                <CardBody><div className="flex justify-center py-10"><Loader2Icon className="h-5 w-5 animate-spin text-gray-400" /></div></CardBody>
            </Card>
        );
    }
    if (state === "error") {
        return (
            <Card>
                <CardHeader title="WhatsApp" description="Couldn't load the connection state." />
                <CardBody className="pt-0"><Button size="sm" variant="outline" onClick={() => void load()}>Try again</Button></CardBody>
            </Card>
        );
    }

    const onPlatform = state.onPlatformNumber === true;
    const unsubmitted = state.templates.filter((t) => t.status === "not_submitted" || t.status === "rejected").length;
    const rejected = state.templates.filter((t) => t.status === "rejected").length;
    const showWhatsapp = show !== "templates";
    // The Setup page lists the steps itself, so its WhatsApp step shows the number alone (#4796).
    const showChecklist = !show || show === "whatsapp";
    const showTemplates = !show || show === "templates";
    const onMigrate = () => (state.oneWayDoorAcceptedAt ? void migrate() : setConsentOpen(true));

    return (
        <div className="space-y-5">
            <ConsentDialog open={consentOpen} busy={busy === "migrate"} onCancel={() => setConsentOpen(false)} onConfirm={() => void migrate()} />

            {showWhatsapp && (<>
            <Card>
                <CardHeader
                    title="WhatsApp number"
                    icon={PhoneIcon}
                    description={onPlatform ? "Customers reach you through ourteam's number, with a link that routes them to you." : "The number your customers already message."}
                    aside={<StageChip stage={state.onboardingStage} />}
                />
                <CardBody className="space-y-3 pt-0">
                    {/* From THIS endpoint. The merchant's own typed number lives
                        on the details card; this is the number the receptionist
                        actually answers on, and the two can differ. */}
                    <div className="flex items-baseline justify-between gap-3">
                        <div>
                            <p className="text-sm font-medium text-gray-900">{state.displayNumber}</p>
                            <p className="text-xs text-gray-500">{onPlatform ? "ourteam's platform number" : `Answers on ${state.number}`}</p>
                        </div>
                        <TagPill tone={state.connected ? "green" : "amber"}>
                            {state.connected ? "Connected" : "Not connected"}
                        </TagPill>
                    </div>
                    {typedNumber && typedNumber !== state.displayNumber && (
                        <p className="text-xs text-gray-500">
                            The number on your business details, {typedNumber}, is what customers know you by; it is not yet the number the receptionist answers on.
                        </p>
                    )}

                    {onPlatform ? (
                        <>
                            <p className="text-xs text-gray-600">
                                Using ourteam's WhatsApp number for now, so there is nothing to move. Share this link — on Instagram, your Google profile, a QR code — and customers land with you:
                            </p>
                            {state.entryLink
                                ? <CopyLink value={state.entryLink} />
                                : <p className="text-xs text-amber-800">No entry link yet — it appears once the platform number is ready.</p>}
                        </>
                    ) : (
                        <>
                            {/* Visible in BOTH states. It is the most consequential
                                fact on the screen, and a merchant who accepted it
                                three weeks ago is the one most likely to have
                                forgotten. */}
                            <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200">
                                <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                                <p className="text-xs text-amber-900">
                                    Once this number moves onto the WhatsApp Business API it leaves the WhatsApp Business app on
                                    your phone. Your team works from the staff group and this console instead. Moving back means
                                    contacting Meta.
                                    {state.oneWayDoorAcceptedAt && <span className="mt-1 block font-medium">You accepted this on {when(state.oneWayDoorAcceptedAt)}.</span>}
                                </p>
                            </div>
                            {!state.checklist.numberMigrated && (
                                <Button size="sm" onClick={onMigrate} disabled={busy === "migrate"}>
                                    {busy === "migrate" ? "Starting…" : "Start the connection"}
                                </Button>
                            )}
                        </>
                    )}
                </CardBody>
            </Card>

            {showChecklist && (<>
            {/* #setup: where the "Setup is not finished" banner's link lands. */}
            <Card id="setup" className="scroll-mt-6">
                <CardHeader title="Setup" icon={ListChecksIcon} description="What is done and what is still outstanding." />
                <CardBody className="pt-0">
                    {/* The rows and marks of Oura's onboarding Steps (components/onboarding/StepsList.tsx):
                        green check done, a blue ring on the next step to do, an empty ring after it. */}
                    <ol className="list-plain m-0 divide-y divide-gray-100 p-0">
                        {SETUP_CHECKLIST.map(({ key, label }) => {
                            const done = state.checklist[key];
                            const next = !done && SETUP_CHECKLIST.find((c) => !state.checklist[c.key])?.key === key;
                            const fact = setupFact(key, state);
                            return (
                                <li key={key} className="flex min-h-11 items-center gap-3">
                                    <span aria-hidden className="flex w-5 shrink-0 justify-center"><SetupMark done={done} next={next} /></span>
                                    <span className={cn(TYPE.tb, "min-w-0 flex-1", done || next ? "text-gray-900" : "text-gray-500")}>
                                        {label}
                                        <span className="sr-only">, {done ? "done" : next ? "next" : "not yet"}</span>
                                    </span>
                                    {fact && <span className={cn(TYPE.tc, "shrink-0 whitespace-nowrap tabular-nums", next ? "font-medium text-blue-700" : "text-gray-500")}>{fact}</span>}
                                </li>
                            );
                        })}
                    </ol>
                </CardBody>
            </Card>

            </>)}
            </>)}

            {showTemplates && (
            <Card>
                <CardHeader
                    title="Message templates"
                    icon={FileTextIcon}
                    description="Anything sent outside WhatsApp's 24-hour window needs Meta's approval first. Each template's wording is a follow-up in Conversation; the morning digest to your team is ours."
                    aside={followUpsHref ? <PillLink to={followUpsHref} size="sm" icon={PencilLineIcon}>Edit the wording</PillLink> : undefined}
                />
                <CardBody className="pt-0">
                    <ul className="list-plain divide-y divide-gray-100">
                        {state.templates.map((t) => {
                            const s = TEMPLATE_STATUS[t.status] ?? { label: t.status, tone: "gray" as Tone };
                            return (
                                <li key={t.key} className="flex items-start justify-between gap-3 py-2">
                                    <div className="min-w-0">
                                        <p className="text-sm text-gray-900">{TEMPLATE_LABEL[t.key] ?? t.key}</p>
                                        {t.status === "rejected" && t.rejectionReason && (
                                            <p className="mt-0.5 flex items-start gap-1 text-xs text-rose-700">
                                                <XCircleIcon className="mt-0.5 h-3 w-3 shrink-0" /> {t.rejectionReason}
                                            </p>
                                        )}
                                    </div>
                                    <TagPill tone={s.tone}>{s.label}</TagPill>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="mt-3 flex justify-end border-t border-gray-100 pt-3">
                        <Button size="sm" onClick={() => void submit()} disabled={unsubmitted === 0 || busy === "submit"}>
                            <SendIcon />
                            {busy === "submit" ? "Submitting…" : unsubmitted ? (rejected ? `Resubmit ${unsubmitted} for approval` : `Submit ${unsubmitted} for approval`) : "All submitted"}
                        </Button>
                    </div>
                </CardBody>
            </Card>
            )}
        </div>
    );
}
